import { ErrorMessage } from '@occc-app/lib/ErrorMessage';

/* Action Types */

const CONSTRAINT_COMPONENT_ERR = 'hq/Offer/Constraints/CONSTRAINT_COMPONENT_ERR';
const CONSTRAINT_COMPONENT_OK = 'hq/Offer/Constraints/CONSTRAINT_COMPONENT_OK';
const CONSTRAINT_COMPONENTS_ERR = 'hq/Offer/Constraint/CONSTRAINT_COMPONENTS_ERR';
const CONSTRAINT_COMPONENTS_OK = 'hq/Offer/Constraint/CONSTRAINT_COMPONENTS_OK';
const CONSTRAINT_CONFIGURATION_UPDATE = 'hq/Offer/Constraints/CONSTRAINT_CONFIGURATION_UPDATE';
const CONSTRAINT_TOGGLE_UPDATE = 'hq/Offer/Constraints/CONSTRAINT_TOGGLE_UPDATE';
const REQUEST_CONSTRAINT = 'hq/Offer/Constraints/REQUEST_CONSTRAINT';
const REQUEST_CONSTRAINT_ERR = 'hq/Offer/Constraints/REQUEST_CONSTRAINT_ERR';
const REQUEST_CONSTRAINT_OK = 'hq/Offer/Constraints/REQUEST_CONSTRAINT_OK';
const REQUEST_CONSTRAINTS_OK = 'hq/Offer/Constraints/REQUEST_CONSTRAINTS_OK';
const RESET_INITIAL_CONSTRAINTS_STATE = 'hq/Offer/Constraints/RESET_INITIAL_CONSTRAINTS_STATE';

type Types = {
  CONSTRAINT_COMPONENT_ERR: typeof CONSTRAINT_COMPONENT_ERR;
  CONSTRAINT_COMPONENT_OK: typeof CONSTRAINT_COMPONENT_OK;
  CONSTRAINT_COMPONENTS_ERR: typeof CONSTRAINT_COMPONENTS_ERR;
  CONSTRAINT_COMPONENTS_OK: typeof CONSTRAINT_COMPONENTS_OK;
  CONSTRAINT_CONFIGURATION_UPDATE: typeof CONSTRAINT_CONFIGURATION_UPDATE;
  CONSTRAINT_TOGGLE_UPDATE: typeof CONSTRAINT_TOGGLE_UPDATE;
  REQUEST_CONSTRAINT: typeof REQUEST_CONSTRAINT;
  REQUEST_CONSTRAINT_ERR: typeof REQUEST_CONSTRAINT_ERR;
  REQUEST_CONSTRAINT_OK: typeof REQUEST_CONSTRAINT_OK;
  REQUEST_CONSTRAINTS_OK: typeof REQUEST_CONSTRAINTS_OK;
  RESET_INITIAL_CONSTRAINTS_STATE: typeof RESET_INITIAL_CONSTRAINTS_STATE;
};

const types: Types = {
  CONSTRAINT_COMPONENT_ERR,
  CONSTRAINT_COMPONENT_OK,
  CONSTRAINT_COMPONENTS_ERR,
  CONSTRAINT_COMPONENTS_OK,
  CONSTRAINT_CONFIGURATION_UPDATE,
  CONSTRAINT_TOGGLE_UPDATE,
  REQUEST_CONSTRAINT,
  REQUEST_CONSTRAINT_ERR,
  REQUEST_CONSTRAINT_OK,
  REQUEST_CONSTRAINTS_OK,
  RESET_INITIAL_CONSTRAINTS_STATE,
};

// Constraints
export const REQUIRES_ACTIVATION = 'requiresActivation';
export const LOCATION_IN_STATE = 'locationInState';

export type ConstraintKeys = typeof REQUIRES_ACTIVATION | typeof LOCATION_IN_STATE;

export type RequiresActivation = {
  type: typeof REQUIRES_ACTIVATION;
  active: boolean;
  mustFulfillWithinDays: number;
  mustFulfillWithinDaysActive: boolean;
};

export type LocationInState = {
  active: boolean;
  states: string[];
  type: typeof LOCATION_IN_STATE;
};

export type Constraints = RequiresActivation | LocationInState;

// Actions
type ConstraintComponentERR = (
  constraintType: ConstraintKeys,
  errorMessages: ErrorMessage[],
  error: Error,
) => {
  type: typeof CONSTRAINT_COMPONENT_ERR;
  constraintType: ConstraintKeys;
  errorMessages: ErrorMessage[];
  error: Error;
};

type ConstraintComponentOK = (
  constraintType: ConstraintKeys,
  id: string,
) => {
  type: typeof CONSTRAINT_COMPONENT_OK;
  constraintType: ConstraintKeys;
  id: string;
};

type ConstraintComponentsERR = (
  errorMessages: ErrorMessage[],
  error: Error,
) => {
  type: typeof CONSTRAINT_COMPONENTS_ERR;
  errorMessages: ErrorMessage[];
  error: Error;
};

type ConstraintComponentsOK = () => {
  type: typeof CONSTRAINT_COMPONENTS_OK;
};

type ConstraintConfigurationUpdate = (
  constraintConfigurationKey: ConstraintKeys,
  key: string,
  value: any,
) => {
  type: typeof CONSTRAINT_CONFIGURATION_UPDATE;
  constraintConfigurationKey: ConstraintKeys;
  key: string;
  value: any;
};

type ConstraintToggleUpdate = (constraintConfigurationKey: ConstraintKeys) => {
  type: typeof CONSTRAINT_TOGGLE_UPDATE;
  constraintConfigurationKey: ConstraintKeys;
};

type RequestConstraint = (
  id: string,
  constraintType: ConstraintKeys,
) => {
  type: typeof REQUEST_CONSTRAINT;
  id: string;
  constraintType: ConstraintKeys;
};

type RequestConstraintERR = (
  errorMessages: ErrorMessage[],
  error: Error,
) => {
  type: typeof REQUEST_CONSTRAINT_ERR;
  errorMessages: ErrorMessage[];
  error: Error;
};

type RequestConstraintOK = (selectedConstraint: Constraints) => {
  type: typeof REQUEST_CONSTRAINT_OK;
  selectedConstraint: Constraints;
};

type RequestConstraintsOK = () => {
  type: typeof REQUEST_CONSTRAINTS_OK;
};

type ResetInitialConstraintsState = (constraints: Constraints) => {
  type: typeof RESET_INITIAL_CONSTRAINTS_STATE;
  constraints: Constraints;
};

export type Action =
  | ReturnType<ConstraintComponentERR>
  | ReturnType<ConstraintComponentOK>
  | ReturnType<ConstraintComponentsERR>
  | ReturnType<ConstraintComponentsOK>
  | ReturnType<ConstraintConfigurationUpdate>
  | ReturnType<ConstraintToggleUpdate>
  | ReturnType<RequestConstraint>
  | ReturnType<RequestConstraintERR>
  | ReturnType<RequestConstraintOK>
  | ReturnType<RequestConstraintsOK>
  | ReturnType<ResetInitialConstraintsState>;

export type ActionCreators = {
  ConstraintComponentERR: ConstraintComponentERR;
  ConstraintComponentOK: ConstraintComponentOK;
  ConstraintComponentsERR: ConstraintComponentsERR;
  ConstraintComponentsOK: ConstraintComponentsOK;
  ConstraintConfigurationUpdate: ConstraintConfigurationUpdate;
  ConstraintToggleUpdate: ConstraintToggleUpdate;
  RequestConstraint: RequestConstraint;
  RequestConstraintERR: RequestConstraintERR;
  RequestConstraintOK: RequestConstraintOK;
  RequestConstraintsOK: RequestConstraintsOK;
  ResetInitialConstraintsState: ResetInitialConstraintsState;
};

/* State Types */
type OfferComponentMetaData = {
  error: Error | null;
  id: string;
  dirty: boolean;
  submitting: boolean;
};

type RequiresActivationErrorMessage = {
  property?: 'require_reactivation' | 'must_fulfill_within_days';
  message: string;
};

type RequiresActivationState = OfferComponentMetaData &
  RequiresActivation & {
    errorMessages: RequiresActivationErrorMessage[];
  };

type LocationInStateErrorMessage = {
  property?: 'states';
  message: string;
};

type LocationInStateState = OfferComponentMetaData &
  LocationInState & {
    errorMessages: LocationInStateErrorMessage[];
  };
export type ConstraintState = RequiresActivationState | LocationInStateState;

export type State = {
  requiresActivation: RequiresActivationState;
  locationInState: LocationInStateState;
  error: Error | null;
  errorMessages: ErrorMessage[];
  requesting: boolean;
};

export default types;
